import React, { useState, useMemo, useEffect } from 'react';
import { Button, DialogContent, CircularProgress } from '@mui/material';
import styles from './SelectMembersDialog.module.scss';
import BaseDialog from '../DialogWrappers/BaseDialog';
import SearchBar from '../atoms/SearchBar';
import MultiSelectList from '../composites/MultiSelectList';
import { getFormalDateString, getFullName } from '../../utils/dashboardUtils';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';

import type { TrainerDashboardData } from '../../appState/appInitialState';
import type { Group, Member } from '../../types';

export interface AddGroupMembersDialogProps {
  group: Group;
  allGymMembers: Member[];
  allGymTrainers: TrainerDashboardData[];
  isOpen: boolean;
  onSubmit: (currentGroupData: Group, newGroupData: Group) => Promise<boolean>;
  onClose: () => void;
}

const AddGroupMembersDialog: React.FC<AddGroupMembersDialogProps> = ({
  group,
  allGymMembers,
  allGymTrainers,
  onClose,
  onSubmit,
  isOpen,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(false);

  const filteredMembers = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    return allGymMembers
      .filter(
        (member) =>
          member.isActive &&
          (!normalizedQuery ||
            getFullName(member).toLowerCase().includes(normalizedQuery)),
      )
      .sort((a, b) => {
        // First sort by whether the member is selected or not
        const aSelected = selectedMembers.some((member) => member.id === a.id);
        const bSelected = selectedMembers.some((member) => member.id === b.id);

        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;

        // Then sort by first name
        return (
          a.firstName
            ?.toLowerCase()
            .localeCompare(b.firstName?.toLowerCase() || '') || -1
        );
      });
  }, [searchQuery, allGymMembers, selectedMembers]);

  // Initialize selectedMembers with current group members when dialog opens
  useEffect(() => {
    if (isOpen) {
      const currentGroupMembers = allGymMembers.filter(
        (member) => group.memberIds.includes(member.id) && member.isActive,
      );
      setSelectedMembers(currentGroupMembers);
      setLoading(false);
    }
  }, [isOpen, group.memberIds, allGymMembers]);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const updatedGroup = {
        ...group,
        memberIds: selectedMembers.map(({ id }) => id),
      };

      const success = await onSubmit(group, updatedGroup);
      if (success) {
        onClose();
      }
    } catch (error) {
      console.error('Error updating group members:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <BaseDialog
      onClose={onClose}
      isOpen={isOpen}
      title="Add Members"
      actions={
        <Button
          className={styles.submit}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={20} color="primary" />
          ) : (
            <span className={styles.submitText}>Add Members</span>
          )}
        </Button>
      }
    >
      <DialogContent className={styles.selectMembersDialog}>
        <div className={styles.searchHeading}>
          <PersonOutlineOutlinedIcon color="primary" />
          <div className={styles.memberName}>Search New Member</div>
        </div>
        <SearchBar
          className={styles.search}
          placeholder="Search New Member"
          setSearchQuery={setSearchQuery}
        />
        <MultiSelectList
          listClassName={styles.list}
          items={filteredMembers}
          selectedItems={selectedMembers}
          setSelectedItems={setSelectedMembers}
          EmptyStateComponent={() => null}
          ItemComponent={({ data }) => (
            <div className={styles.memberMultiSelectItem}>
              <div className={styles.memberNameWrapper}>
                <PersonOutlineOutlinedIcon color="primary" />
                <div className={styles.memberName}>{getFullName(data)}</div>
              </div>
              <div>
                {`${data.createdDate ? getFormalDateString(new Date(data.createdDate)) : ''}`}
              </div>
              <div>
                {getFullName(
                  allGymTrainers
                    .map(({ trainer }) => trainer)
                    .find(({ id }) => id === data.trainerIds[0]),
                )}
              </div>
            </div>
          )}
        />
      </DialogContent>
    </BaseDialog>
  );
};

export default AddGroupMembersDialog;
